import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import type { GeneratorConfigV1, Step } from '../../types/generator-config';
import { createStep, randomId } from '../../lib/fieldFactory';
import { StepEditor } from './StepEditor';

interface StepsSectionProps {
    config: GeneratorConfigV1;
    onChange: (updater: (prev: GeneratorConfigV1) => GeneratorConfigV1) => void;
}

export function StepsSection({ config, onChange }: StepsSectionProps) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
    );

    const updateSteps = (steps: Step[]) => {
        onChange((prev) => ({ ...prev, steps }));
    };

    const setStep = (next: Step) => {
        updateSteps(config.steps.map((s) => (s.id === next.id ? next : s)));
    };

    const addStep = () => {
        const next = createStep({ title: `Étape ${config.steps.length + 1}` });
        updateSteps([...config.steps, next]);
    };

    const removeStep = (stepId: string) => {
        updateSteps(config.steps.filter((s) => s.id !== stepId));
    };

    const duplicateStep = (stepId: string) => {
        const src = config.steps.find((s) => s.id === stepId);
        if (!src) return;
        const copy: Step = {
            ...src,
            id: randomId(),
            title: `${src.title} (copie)`,
            fields: src.fields.map((f) => ({ ...f, id: randomId() })),
        };
        const idx = config.steps.findIndex((s) => s.id === stepId);
        const next = [...config.steps];
        next.splice(idx + 1, 0, copy);
        updateSteps(next);
    };

    const handleDragEnd = (e: DragEndEvent) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIdx = config.steps.findIndex((s) => s.id === active.id);
        const newIdx = config.steps.findIndex((s) => s.id === over.id);
        updateSteps(arrayMove(config.steps, oldIdx, newIdx));
    };

    return (
        <div className="editor-fields">
            <button
                type="button"
                className="btn-ghost steps-add-btn"
                onClick={addStep}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Ajouter une étape
            </button>

            {config.steps.length === 0 ? (
                <div className="steps-empty">
                    <p>
                        Aucune étape pour l'instant. Cliquez sur <strong>Étape</strong>
                        ci-dessus pour commencer à construire votre générateur.
                    </p>
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={config.steps.map((s) => s.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <ul className="steps-list">
                            {config.steps.map((s, idx) => (
                                <StepEditor
                                    key={s.id}
                                    step={s}
                                    index={idx}
                                    config={config}
                                    onChange={setStep}
                                    onRemove={() => removeStep(s.id)}
                                    onDuplicate={() => duplicateStep(s.id)}
                                />
                            ))}
                        </ul>
                    </SortableContext>
                </DndContext>
            )}
        </div>
    );
}
